import { useEffect, useState } from 'react';

import styled from '@emotion/styled';
import { useRouter } from 'next/router';

import PlayerLoadingScripts from 'src/components/consumptionPage/VideoPlayer/player-loading-scripts/PlayerLoadingScripts';
import { newKeysList } from 'src/components/pageMeta/SEOMetaUtils';
import { transformToCamelCase } from 'src/components/pageMeta/transformToCamelCase';
import { useElementOffsetHeight } from 'src/hooks/useElementOffsetHeight';
import { useToggle } from 'src/hooks/useToggle';
import { getPartnerSettingsQueryData } from 'src/services/apiRequests/B2b/B2bPartnerService';
import { gwapigetWithUserToken } from 'src/services/apiRequests/MovieConsumptionPage/FetchUpnextServices';
import { postForSinglePlay } from 'src/services/apiRequests/SinglePlayService';
import { mixpanelObject } from 'src/utils/analytics';
import { logout } from 'src/utils/auth-utils/LogoutHandler';
import {
  getUserTokenFromLS,
  setRefreshTokenInLS,
  setUserIDInLS,
  setUserTokenExpiryInLS,
  setUserTokenInLS
} from 'src/utils/auth-utils/TokenHandler';
import { setUserTypeInLS } from 'src/utils/auth-utils/UserTypeHandler';
import { getUsersAge } from 'src/utils/common';
import { GUEST_TOKEN } from 'src/utils/constants/AppConstants';
import { FeatureFlagConstants } from 'src/utils/constants/utilConstants';
import { isFeatureEnabled } from 'src/utils/RemoteConfig';
import { validateSinglePlayDataResponse } from 'src/utils/responseValidator';
import {
  loadConvivaAdsScripts,
  loadConvivaScripts
} from 'src/utils/scripts/convivaScripts';
import {
  castScriptAdder,
  loadScripts
} from 'src/utils/scripts/thirdPartyScripts';
import {
  getContentAppropriateAge,
  getGuestUserToken
} from 'src/utils/UtilityMethods';

import { useAppStore } from 'src/hooks/useAppStore';
import SimpleStore from 'src/store/SimpleStore';
import {
  deepLinkDataEvent,
  partnerSettingsDataEvent
} from 'src/utils/analytics/mixpanel/mixpanelEventsHelper';
import {
  HTTP_HEADER_KEYS,
  HTTP_URL_PARAMS
} from 'src/utils/constants/HttpMethodsConstants';
import ConsumptionMetaData from '../../components/consumptionPage/ConsumptionMetaData';
import { generateMetaDataProps } from '../../components/consumptionPage/ConsumptionMetaData/ConsumptionMetadataHelper';
import VideoPlayer from '../../components/consumptionPage/VideoPlayer';
import { ContentTypes } from '../../components/thumbnail/types';
import { addAndRemoveDevice } from '../../services/apiRequests/AddAndRemoveDeviceService';
import { fetchTelcoUserToken } from '../../services/apiRequests/Login/telco-user-login/ShortToLongTokenService';
import { UserTokenType } from '../../services/responseTypes/fetchTelcoUserTokenResponse';
import { SinglePlayResponse } from '../../services/responseTypes/singlePlayResponse';
import { MediaBP as MQ } from '../../styles/mediaQuery';
import ConvivaAnalyticHelper from '../../utils/analytics/conviva/ConvivaAnalyticHelper';
import {
  MIXPANEL_AGE,
  MIXPANEL_GENDER,
  MIXPANEL_METHOD,
  MIXPANEL_NEW_APP_LANGUAGE,
  MIXPANEL_NEW_AUTOPLAY_SETTING,
  MIXPANEL_NEW_CONTENT_LANGUAGE,
  MIXPANEL_PAGE_NAME,
  MIXPANEL_PARENT_CONTROL_SETTING,
  MIXPANEL_PARTNER_NAME,
  MIXPANEL_REGISTRING_COUNTRY,
  MIXPANEL_SOURCE,
  MIXPANEL_SUCCESS,
  MIXPANEL_TAB_NAME,
  MIXPANEL_UNIQUE_ID,
  MIXPANEL_USER_TYPE
} from '../../utils/analytics/mixpanel/mixpanelConstants';
import {
  getCookie,
  getLocalStorage,
  getObjectFromLocalStorage,
  getSessionStorage,
  isIos,
  setSessionStorage
} from '../../utils/browserStorageHandler';
import { DynamicObject } from '../../utils/commonTypes';
import {
  BACK_TO_APPWIDGET,
  IS_TELCO_USER,
  PARTNER_NAME,
  PARTNER_SOURCE,
  SETTINGS,
  USER_ID,
  USER_TOKEN,
  UTM_SOURCE,
  VIBLOCKERFLAG
} from '../../utils/constants/localStorageConstants';
import { Routes } from '../../utils/constants/RouterConstants';
import {
  CountryCode,
  PartnerPlatformIds,
  PartnerPlatforms,
  UserTypes
} from '../../utils/constants/stringConstants';
import { getContentLiteDataFromSinglePlayData } from '../../utils/ConsumptionHelper/SPAPIContentLiteMappingHelper';
import { validateRouteParams } from '../../utils/routeHandler';
import {
  gwapiUpNextUrlGenerator,
  singlePlayApiUrlGenerator
} from '../../utils/UrlGenerator';

interface UpnextParams {
  type?: string;
  asset_id?: string;
  translation?: string;
  languages?: string;
  country?: string;
  pc_age?: any;
  series_id?: string;
  season_id?: string;
}

const MetaDataContainer = styled.div<any>`
  ${MQ.TabletPortraitMaxWidth} {
    padding-top: ${(props) => props.playerWrapperHeight}px;
  }
`;
const PlayerWrapper = styled.div`
  ${MQ.TabletPortraitMaxWidth} {
    width: 100%;
    position: fixed;
    z-index: 100;
  }
`;

const B2BConsumption = () => {
  const {
    translations,
    usersLocationData,
    initialLaunchData,
    usersData,
    usersPreferenceData,
    appConfig,
    remoteConfig,
    loadExternalScripts,
    setAppConfig,
    setUsersPreferenceData,
    setExternalScripts
  } = useAppStore();

  const isRequestedByBot = appConfig.isRequestedByBot as boolean;
  const router = useRouter();
  const isUpNext =
    isFeatureEnabled(remoteConfig, FeatureFlagConstants.FEATURE_UPNEXT_API) ||
    false;

  const [isUpNextPending, setIsUpNextPending] = useState(false);
  const [upNextApiState, setUpNextApiState] = useState(false);
  const [isTelcoTokenPending, setIsTelcoTokenPending] = useState(true);
  const [singlePlayB2bData, setSinglePlayB2bData] = useState<
    SinglePlayResponse | DynamicObject | null
  >(null);
  const [upNextContent, setUpNextContent] = useState([]);
  const [showCrew, toggleCrew] = useToggle(false);
  const [playerWrapperRef, playerWrapperHeight] = useElementOffsetHeight();
  const contentId = validateRouteParams(router.query['content-id'] as string);
  const countryCode = usersLocationData?.country_code || 'in';
  const displayLanguages = usersPreferenceData?.display_language || 'en';

  const getUserType = () => {
    const userType = getLocalStorage(USER_TOKEN)
      ? UserTypes.PREMIUM
      : UserTypes.GUEST;
    return userType;
  };
  const trackTelcoMixpanelEvents = () => {
    mixpanelObject[MIXPANEL_UNIQUE_ID] =
      getLocalStorage(USER_ID) || getLocalStorage(GUEST_TOKEN) || 'N/A';
    mixpanelObject[MIXPANEL_USER_TYPE] = getUserType();
    mixpanelObject[MIXPANEL_REGISTRING_COUNTRY] =
      usersData?.registration_country || 'N/A';
    mixpanelObject[MIXPANEL_GENDER] = usersData?.gender || 'N/A';
    mixpanelObject[MIXPANEL_NEW_AUTOPLAY_SETTING] =
      usersPreferenceData?.auto_play || 'N/A';
    mixpanelObject[MIXPANEL_NEW_APP_LANGUAGE] =
      usersPreferenceData?.display_language || 'en';
    mixpanelObject[MIXPANEL_NEW_CONTENT_LANGUAGE] =
      usersPreferenceData?.content_language || 'en';
    mixpanelObject[MIXPANEL_PARENT_CONTROL_SETTING] =
      usersPreferenceData?.parental_control ||
      usersPreferenceData?.parental_control_v2 ||
      'N/A';
    mixpanelObject[MIXPANEL_AGE] = getUsersAge(usersData?.birthday);

    // event track property
    mixpanelObject[MIXPANEL_SUCCESS] = true;
    mixpanelObject[MIXPANEL_METHOD] = 'Mobile';
    mixpanelObject[MIXPANEL_PARTNER_NAME] = getLocalStorage(PARTNER_NAME);

    mixpanelObject[MIXPANEL_SOURCE] = getLocalStorage(PARTNER_NAME);
    mixpanelObject[MIXPANEL_PAGE_NAME] = window.location.pathname;
    mixpanelObject[MIXPANEL_TAB_NAME] = window.location.pathname.split('/')[1];
  };

  const initializeConviva = () => {
    if (window.Conviva) {
      if (window.convivaAnalytic) {
        return;
      }
      window.convivaAnalytic = ConvivaAnalyticHelper;
      ConvivaAnalyticHelper.initializeConviva();
    } else {
      setTimeout(initializeConviva, 500);
    }
  };

  //TODO: same code is used in _app.tsx for TVOD IOS handling, Can be improved with re-usability
  const fetchUserAuthToken = async (tag: string) => {
    try {
      const token: UserTokenType = await fetchTelcoUserToken(tag);
      //TODO: Error handling as it can respond with 401 when tag is expired.
      if (token?.user?.token) {
        setUserTokenInLS(token.user.token);
        setUserIDInLS(token.user.token);
        setUserTokenExpiryInLS(token.user.expires_in);
        setRefreshTokenInLS(token.user.refresh_token);
        setIsTelcoTokenPending(false);
      }
      return token?.user?.token;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  useEffect(() => {
    const {
      tag,
      partner,
      utm_source: utmSource,
      partner_platform
    } = router.query;
    /**
     * 1. Check if user is already loggedIn. If so, clear all previous sessions.
     * 2. Fetch and store Guest User token as it is required to call Short to long token (Telco user token)
     * 3. Fetch user auth token for Telco users and store in LS.
     */
    if (tag) {
      if (getUserTokenFromLS()) {
        logout();
      }
      (async () => {
        await getGuestUserToken();
        await fetchUserAuthToken(tag as string);

        let partnerName = '';
        let partnerId;
        partnerName = partnerHandler(partner, partner_platform);
        deepLinkDataEvent(contentId, partnerName);
        if (utmSource) {
          if (utmSource === PartnerPlatforms.TATASKY) {
            setSessionStorage(UTM_SOURCE, PartnerPlatforms.TATASKY_PARTNER);
          } else {
            setSessionStorage(UTM_SOURCE, utmSource);
          }
        }

        try {
          //Partner Settings will be undefined in case of tataskybinge
          const { partnerSettings } = await getPartnerSettingsQueryData({
            partnerKey: partnerName
          });

          partnerName = partnerSettings?.partner?.partnerName;
          partnerId = partnerSettings?.partner?.partnerId as number;
          partnerSettingsDataEvent(partnerName, partnerId);
          setSessionStorage(PARTNER_NAME, partnerName);

          partnerSettings &&
            setUsersPreferenceData({
              partner: partnerSettings
            });
          partner_platform &&
            setSessionStorage(PARTNER_SOURCE, partner_platform);
        } catch {
          sessionHandler(partnerName);
        }

        if (
          partnerId === PartnerPlatformIds.VIMTVID ||
          partnerId === PartnerPlatformIds.VIAPP ||
          partnerId === PartnerPlatformIds.VIAPP_PT
        ) {
          setAppConfig({ isViUser: true });
          setSessionStorage(BACK_TO_APPWIDGET, true);
          setSessionStorage(VIBLOCKERFLAG, true);
        } else {
          setAppConfig({ isViUser: false });
          setSessionStorage(BACK_TO_APPWIDGET, false);
          setSessionStorage(VIBLOCKERFLAG, false);
        }

        setSessionStorage(IS_TELCO_USER, true);
        setUserTypeInLS(getUserType());

        const shortPathnameToShow = router.asPath.split('?')[0];
        router.replace(router.asPath, shortPathnameToShow, { shallow: true });
        deepLinkDataEvent(
          contentId,
          partnerName,
          window.location.pathname,
          true
        );
        trackTelcoMixpanelEvents();

        getSinglePlayDetails({
          prefferedAudioStream: undefined
        });
      })();
    } else {
      //To handle the case where URL does not have tag (Guest B2B users)
      setIsTelcoTokenPending(false);
    }
  }, []);

  const partnerHandler = (
    partner: string | string[] | undefined,
    partner_platform: string | string[] | undefined
  ) => {
    let partnerName = '';
    if (partner) {
      partnerName = partner as string;
    } else {
      partnerName = partner_platform as string;
    }
    return partnerName;
  };

  const sessionHandler = (partnerName: any) => {
    if (partnerName === PartnerPlatforms.TATASKY) {
      setSessionStorage(PARTNER_NAME, PartnerPlatforms.TATASKY_PARTNER);
    } else if (partnerName === PartnerPlatforms.VIMTV_ANDROID) {
      setSessionStorage(PARTNER_NAME, PartnerPlatforms.VIMTVAPP);
    } else {
      setSessionStorage(PARTNER_NAME, partnerName);
    }
  };
  //TODO: We can remove the commented code. Kept it to discuss later, why that way is not working.

  // // To short the display url. Will remove query param and just show pathname.
  useEffect(() => {
    loadConvivaScripts();
    loadConvivaAdsScripts();
    if (!isRequestedByBot) {
      if (!isIos()) {
        castScriptAdder();
      }
      initializeConviva();
    }
  }, []);

  useEffect(() => {
    if (initialLaunchData?.platform_token.token && !isTelcoTokenPending) {
      getSinglePlayDetails({
        prefferedAudioStream: undefined
      });
    }
  }, [isTelcoTokenPending]);

  const getSinglePlayDetails = async (id: { prefferedAudioStream: any }) => {
    let displayLanguage = '';
    let contentLanguages = '';
    if (getLocalStorage(SETTINGS) && getObjectFromLocalStorage(SETTINGS)) {
      const userSettings = getObjectFromLocalStorage(SETTINGS);
      displayLanguage = userSettings.display_language;
      contentLanguages = userSettings.content_language;
    }
    const isChannel = router.asPath.includes(`${Routes.B2B_LIVE_TV}/`);
    // carries all the details for the subsequent calls made once the single play api is success
    const obj = {
      id: contentId,
      country:
        usersLocationData?.country_code ||
        SimpleStore.get(HTTP_URL_PARAMS.COUNTRY_CODE) ||
        CountryCode.IN,
      isVmax: false,
      region: usersLocationData?.state_code || '',
      translation: displayLanguage,
      languages: contentLanguages,
      userType: getUserType(),
      contentId: contentId,
      isLoggedIn: true,
      gender: usersData?.gender,
      user: getSessionStorage(PARTNER_NAME),
      age: getUsersAge(usersData?.birthday),
      parentalPinStatus: true,
      isChannel,
      prefferedAudioStream: (id && id.prefferedAudioStream) || ''
    };
    const isParentalControlSet = false;
    const parentalControlValue = undefined;
    const url = await singlePlayApiUrlGenerator(
      {
        ...obj,
        isParentalControlSet,
        parentalControlValue
      },
      remoteConfig
    );
    try {
      const access_token = {
        'x-access-token': SimpleStore.get(HTTP_HEADER_KEYS.X_ACCESS_TOKEN)
      };
      let response = await postForSinglePlay(url, access_token);
      if (response && response.error_code === '3608') {
        await addAndRemoveDevice();
        // call when successful; implement a counter
        response = await postForSinglePlay(url, obj);
      }
      if (
        response &&
        response.error_code === '401' &&
        getLocalStorage(USER_TOKEN)
      ) {
        // renewUserToken();
      } else if (validateSinglePlayDataResponse(response)) {
        setSinglePlayB2bData(response);
      }
    } catch (error) {
      console.error('SinglePlay API failure: ', error);
    }
  };

  // const updateAudioStream = (audioLang: any) => {
  //   getSinglePlayDetails({ prefferedAudioStream: audioLang });
  // };

  const fetchUpNextDetails = async (params: UpnextParams) => {
    try {
      if (
        (getLocalStorage(SETTINGS) && getObjectFromLocalStorage(SETTINGS)) ||
        (getCookie(SETTINGS) && JSON.parse(getCookie(SETTINGS) as string))
      ) {
        const usersPreferenceData =
          getObjectFromLocalStorage(SETTINGS) ||
          JSON.parse(getCookie(SETTINGS) as string);
        const contentAppropriateAge =
          getContentAppropriateAge(usersPreferenceData) || null;
        params.pc_age = contentAppropriateAge;
      }
      const getMovieUrl = gwapiUpNextUrlGenerator(params);
      const upNextData = await gwapigetWithUserToken(getMovieUrl);
      const upnextItems = transformToCamelCase(upNextData.items, newKeysList);
      return upnextItems;
    } catch (error) {
      console.error(
        'Something went wrong while fetching GWAPI upnext reco: ',
        error
      );
    }
  };

  const getUpNextData = async () => {
    let contentLanguages = '';
    if (
      (getLocalStorage(SETTINGS) && getObjectFromLocalStorage(SETTINGS)) ||
      (getCookie(SETTINGS) && JSON.parse(getCookie(SETTINGS) as string))
    ) {
      const userSettings =
        getObjectFromLocalStorage(SETTINGS) ||
        JSON.parse(getCookie(SETTINGS) as string);
      contentLanguages = userSettings.content_language;
    }
    const params = {
      type:
        singlePlayB2bData?.showDetails?.asset_subtype ||
        singlePlayB2bData?.assetDetails?.asset_subtype,
      asset_id: contentId,
      series_id: singlePlayB2bData?.showDetails?.id,
      season_id: singlePlayB2bData?.showDetails?.season,
      translation: displayLanguages,
      languages: contentLanguages,
      userType: getUserType(),
      country: countryCode
    };
    if (params.asset_id) {
      setUpNextApiState(false);
      setIsUpNextPending(true);
      if (isUpNext) {
        try {
          const data = (await fetchUpNextDetails(params)) || [];
          setUpNextContent(data);
        } catch (error) {
          setUpNextContent([]);
        }
        setUpNextApiState(true);
        setIsUpNextPending(false);
      } else {
        setUpNextContent([]);
      }
    }
  };

  const triggerExternalScript = () => {
    if (!loadExternalScripts) {
      setExternalScripts(true);
      loadScripts();
    }
  };

  return (
    <>
      <PlayerLoadingScripts />
      <div>
        <PlayerWrapper ref={playerWrapperRef}>
          <VideoPlayer
            contentId={contentId}
            singlePlayDetails={singlePlayB2bData}
            getSinglePlayDetails={getSinglePlayDetails}
            setVITvod={undefined}
            callStaledApis={undefined}
            handleParentalPin={undefined}
            setAvodPopupNudgeVisibleAction={undefined}
            subscriptionPlansPending={undefined}
            subscriptionPlansError={undefined}
            getUpNextData={getUpNextData}
            upNextApiState={upNextApiState}
            isUpNextPending={isUpNextPending}
            similarFromReco={upNextContent}
          />
        </PlayerWrapper>
        <MetaDataContainer playerWrapperHeight={playerWrapperHeight}>
          {singlePlayB2bData && !singlePlayB2bData?.error_code ? (
            <ConsumptionMetaData
              {...generateMetaDataProps(
                ContentTypes.B2B,
                getContentLiteDataFromSinglePlayData(singlePlayB2bData),
                translations,
                singlePlayB2bData?.assetDetails?.asset_subtype
              )}
              showCrew={showCrew}
              toggleCrew={toggleCrew}
              spaceX={'24'}
            />
          ) : null}
        </MetaDataContainer>
      </div>
    </>
  );
};

export default B2BConsumption;
